import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import Close from '@mui/icons-material/Close';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import LoadMoreWrapper from 'components-v2/atoms/LoadMoreWrapper';
import { QA_AUTHEN_ICON, QA_CUSTOMER_AVATAR_ICON, QA_EMPLOYEE_AVATAR_ICON, QA_REPLY_ICON } from 'constants/Images';
import Image from 'next/image';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DateTimeUtils from 'utils/DateTimeUtils';
import { getLinkCDNImage } from 'utils/ImageUtils';
import CommentForm, { ReplyData } from './CommentForm';
import styles from './styles.module.css';

const PHOTO_ERROR_SRC = '/images/photo_error.svg';
const SWIPE_THRESHOLD = 50;

const CommentImage = ({ url, onClick }: { url: string; onClick: () => void }) => {
  const [errored, setErrored] = useState(false);
  const src = errored ? PHOTO_ERROR_SRC : getLinkCDNImage(url);

  return (
    <button type="button" className={styles.commentImageItem} onClick={onClick}>
      <Image src={src} width={100} height={100} loading="lazy" alt="" onError={() => setErrored(true)} />
    </button>
  );
};

const CommentImagesLightbox = ({
  images,
  initialIndex,
  onClose,
}: {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [errored, setErrored] = useState<Record<number, boolean>>({});
  const touchStartX = useRef<number | null>(null);
  const hasMultiple = images.length > 1;

  const showPrev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const showNext = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (hasMultiple && e.key === 'ArrowLeft') showPrev();
      else if (hasMultiple && e.key === 'ArrowRight') showNext();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [hasMultiple, showPrev, showNext, onClose]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || !hasMultiple) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx > 0) showPrev();
      else showNext();
    }
    touchStartX.current = null;
  };

  if (typeof document === 'undefined') return null;

  const currentSrc = errored[index] ? PHOTO_ERROR_SRC : getLinkCDNImage(images[index]);

  const overlay = (
    <div className={styles.lightboxOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" className={styles.lightboxClose} onClick={onClose} aria-label="Đóng">
        <Close />
      </button>
      {hasMultiple && (
        <button
          type="button"
          className={clsx(styles.lightboxNav, styles.lightboxNavPrev)}
          onClick={(e) => {
            e.stopPropagation();
            showPrev();
          }}
          aria-label="Ảnh trước"
        >
          <NavigateBefore />
        </button>
      )}
      <div
        className={styles.lightboxContent}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentSrc}
          alt=""
          className={styles.lightboxImage}
          onError={() => setErrored((prev) => ({ ...prev, [index]: true }))}
        />
      </div>
      {hasMultiple && (
        <>
          <button
            type="button"
            className={clsx(styles.lightboxNav, styles.lightboxNavNext)}
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Ảnh kế"
          >
            <NavigateNext />
          </button>
          <div className={styles.lightboxCounter}>
            {index + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
};

export interface ActiveComment {
  id: number | null;
  type: 'replying' | 'idle';
}

interface LoadMoreReply {
  restNumber: number;
  parentCommentID: number | null;
  totalReply: number;
}

interface Props {
  id: string | number;
  parentCommentID?: number | null;
  activeComment: ActiveComment;
  setActiveComment: Dispatch<SetStateAction<ActiveComment>>;
  commenter: {
    customerID?: number;
    employeeID?: number;
    fullname: string;
    email: string;
    isModerator: boolean;
  };
  content: string;
  images?: string[];
  createdTime: string;
  replies?: any[];
  depth?: number;
  depthStep?: number;
  isChild?: boolean;
  type?: 'COMMENT' | 'REPLY';
  totalReply?: number;
  onLike?: (name: string) => void;
  onReply?: (data: ReplyData, parentCommentID: number) => void;
  onLoadMoreReply?: (data: LoadMoreReply) => void;
}

const CommentItem = (props: Props) => {
  const {
    id,
    parentCommentID = null,
    activeComment,
    setActiveComment,
    commenter,
    content,
    images = [],
    createdTime,
    replies = [],
    isChild = false,
    type = 'COMMENT',
    depth = 0,
    depthStep = 10,
    totalReply = 0,
    onLike,
    onReply,
    onLoadMoreReply,
  } = props;
  const canReply = Boolean(id);
  const isReplying = activeComment && activeComment.id === id && activeComment.type === 'replying';
  const commentPaddingLeft = type === 'COMMENT' ? 4 : 0;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const info = {
    COMMENT: {
      name: commenter.fullname,
      image: QA_CUSTOMER_AVATAR_ICON,
    },
    REPLY: {
      name: commenter.fullname,
      image: commenter.isModerator ? QA_EMPLOYEE_AVATAR_ICON : QA_CUSTOMER_AVATAR_ICON,
    },
  };

  return (
    <ListItem
      id={`${type}-${id}`}
      disableGutters
      className={clsx(styles.commentItem, {
        [styles.subCommentItem]: isChild,
        [styles.hasReply]: isReplying,
      })}
      style={{ paddingLeft: depth * depthStep }}
    >
      <Box className={styles.commentInner}>
        <Box className={clsx(styles.commentAvatar, { [styles.hasChildBranch]: replies.length > 0 })}>
          {/* Also update WIDTH or HEIGHT of avatar in css file if you update WIDTH, HEIGHT here  */}
          <Image className={styles.commentAvatarImg} src={info[type].image} width={24} height={24} loading="lazy" />
        </Box>
        <Box className={styles.commentInfo}>
          <Typography variant="h4" className={styles.commentName}>
            {info[type].name}
            {type === 'REPLY' && <Image src={QA_AUTHEN_ICON} width={12} height={12} loading="lazy" />}
          </Typography>
          <div
            className={styles.commentContent}
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
          {images?.length > 0 && (
            <Box className={styles.commentImages}>
              {images.map((url, idx) => (
                <CommentImage key={url} url={url} onClick={() => setLightboxIndex(idx)} />
              ))}
            </Box>
          )}
          {lightboxIndex !== null && (
            <CommentImagesLightbox
              images={images}
              initialIndex={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
            />
          )}
          <div className={styles.commentUtils}>
            <span className={styles.commentTime}>{DateTimeUtils.getTimeAgo(createdTime)}</span>
            {onLike && (
              <div role="button" className={styles.commentLike} onClick={() => onLike(info[type].name)}>
                Hữu ích
              </div>
            )}
            {canReply && (
              <div role="button" className={styles.commentReply} onClick={() => setActiveComment({ id, type: 'replying' })}>
                <Image src={QA_REPLY_ICON} width={20} height={20} loading="lazy" alt="reply icon" />
                Trả lời
              </div>
            )}
          </div>
        </Box>
      </Box>
      {isReplying && onReply && (
        <CommentForm
          className={clsx({ [styles.hasReplies]: replies.length > 0 })}
          type={type}
          style={{ paddingLeft: commentPaddingLeft * depthStep }}
          image={info.COMMENT.image}
          replyName={info[type].name}
          onSubmit={(data) => onReply(data, parentCommentID || +id)}
        />
      )}
      <LoadMoreWrapper
        className={styles.loadMoreReply}
        isBlur={false}
        total={totalReply}
        totalVisible={replies.length}
        onLoadMoreComment={(num) => {
          // eslint-disable-next-line no-unused-expressions
          onLoadMoreReply && onLoadMoreReply({ restNumber: num, parentCommentID, totalReply });
        }}
      >
        {replies.length > 0 &&
          replies.map((comment) => (
            <CommentItem
              key={comment.commentID}
              id={comment.commentID}
              activeComment={activeComment}
              setActiveComment={setActiveComment}
              parentCommentID={comment.parentCommentID}
              commenter={comment.commenter}
              content={comment.content}
              images={comment.images}
              createdTime={comment.createdTime}
              replies={comment.replies}
              onReply={onReply}
              depth={4}
              type="REPLY"
              isChild
            />
          ))}
      </LoadMoreWrapper>
    </ListItem>
  );
};

export default CommentItem;
